import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useRef, useState } from "react";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import { useAppDispatch, useAppSelector } from "store/hooks/hooks";
import { saveUserLogo, selectFetchedUserLogos } from "store/slices/logosSlice";
import LucideIcon from "utils/LucideIcon";

type ILogoDialogueProps = {
  onClose: () => void;
  setLogoChanged?: React.Dispatch<React.SetStateAction<boolean>>;
};

const LogoUploadeModel: React.FC<ILogoDialogueProps> = ({
  onClose,
  setLogoChanged,
}) => {
  const dispatch = useAppDispatch();
  const userLogos = useAppSelector(selectFetchedUserLogos);
  const [logoModalVisible, setLogoModalVisible] = useState(false);
  const [logoUploadType, setLogoUploadType] = useState("upload");
  const [logoUrl, setLogoUrl] = useState("");
  const [logoName, setLogoName] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [logos, setLogos] = useState(userLogos);
  const [logoPreview, setLogoPreview] = useState<any>();
  const [logoError, setLogoError] = useState("");
  const [nameError, setNameError] = useState("");
  const [isLogoAdding, setIsLogoAdding] = useState(false);
  const logoFileInputRef = useRef<any>();

  const isValidUrl = (string: any) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleSaveLogo = async () => {
    setLogoError("");
    setIsLogoAdding(true);

    if (!logoName.trim()) {
      setLogoError("Logo name is required");
      setIsLogoAdding(false);
      return;
    }

    if (logoName.trim().length > 20) {
      setLogoError("Logo name must be 20 characters or fewer");
      setIsLogoAdding(false);
      return;
    }

    if (logoUploadType === "upload") {
      if (!logoFile) {
        setLogoError("Please select a file to upload");
        setIsLogoAdding(false);
        return;
      }

      try {
        const uploadedUrl = await dispatch(
          saveUserLogo({ file: logoFile, name: logoName })
        ).unwrap();

        const newLogo = {
          id: Date.now(),
          name: logoName,
          url: uploadedUrl,
          type: "upload",
          uploadDate: new Date().toISOString().split("T")[0],
        };

        setLogos((prev: any) => [...prev, newLogo]);
        if (setLogoChanged) {
          setLogoChanged((prev) => !prev);
        }
        onClose();
      } catch (err: any) {
        setLogoError(err.message || "Failed to upload logo.");
      } finally {
        setIsLogoAdding(false);
      }
    }

    if (logoUploadType === "url") {
      if (!logoUrl.trim() || !isValidUrl(logoUrl)) {
        setIsLogoAdding(false);
        setLogoError("Please enter a valid URL");
        return;
      }

      try {
        const newLogo = {
          id: Date.now(),
          name: logoName,
          url: logoUrl,
          type: "url",
          uploadDate: new Date().toISOString().split("T")[0],
        };

        setLogos((prev: any) => [...prev, newLogo]);
        setLogoModalVisible(false);
      } catch (err: any) {
        setLogoError(err.message || "Failed to save logo URL.");
      } finally {
        setIsLogoAdding(false);
      }
    }
  };

  const handleLogoFileChange = (e: any) => {
    const file = e.target.files[0];

    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setLogoError("File size must be less than 5MB");
        return;
      }

      // Check file type (only PNG or JPG/JPEG allowed)
      const validTypes = ["image/png", "image/jpeg"];
      if (!validTypes.includes(file.type)) {
        setLogoError("Invalid file type. Please upload a PNG or JPG image.");
        return;
      }

      setLogoFile(file);
      setLogoError("");

      const reader = new FileReader();
      reader.onload = (e) => setLogoPreview(e.target?.result);
      reader.readAsDataURL(file);
    }
  };

  return (
    <>
      <AnimatePresence>
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm p-4 sm:p-6 md:p-8 !mt-0">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="relative bg-white rounded-lg shadow-xl w-full max-w-3xl flex flex-col border border-gray-200 lg:max-h-[90vh] overflow-x-auto p-4 md:p-8"
          >
            <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
              Add New Logo
            </h2>
            <button
              onClick={onClose}
              disabled={isLogoAdding}
              className="absolute text-end text-gray-600 p-2 rounded-full hover:text-red-500 hover:bg-red-100 z-50 top-1 md:top-3 right-1 md:right-3 duration-300 disabled:cursor-not-allowed disabled:text-gray-500 disabled:hover:bg-white"
            >
              <X size={20} />
            </button>
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Logo Name *
              </label>
              <input
                type="text"
                value={logoName}
                disabled={isLogoAdding}
                onChange={(e) => {
                  const name = e.target.value;

                  if (name.length <= 20) {
                    setLogoName(name);
                    if (name.trim() && !/[a-zA-Z0-9]/.test(name)) {
                      setNameError(
                        "Logo name cannot be only special characters"
                      );
                    } else {
                      setNameError("");
                    }
                  } else {
                    setNameError("Logo name must be 20 characters or fewer");
                  }
                }}
                placeholder="Enter logo name..."
                className="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>

            {nameError && (
              <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-xl">
                <p className="text-red-600 text-sm flex items-center">
                  <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                  {nameError}
                </p>
              </div>
            )}

            {/* File Upload Section */}
            {logoUploadType === "upload" && (
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Select Logo File *
                </label>
                <div
                  onClick={() => logoFileInputRef.current?.click()}
                  className={`border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-blue-400 hover:bg-blue-50 transition-all duration-300 cursor-pointer
                    ${isLogoAdding && "cursor-not-allowed opacity-50"}
                    `}
                >
                  {logoPreview ? (
                    <div className="space-y-4">
                      <img
                        src={logoPreview}
                        alt="Preview"
                        className="max-h-32 mx-auto rounded-lg checkerboard"
                      />
                      <p className="text-sm text-gray-600">
                        Click to change file
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <LucideIcon
                        name="Upload"
                        size={48}
                        className="text-gray-400 hover:text-gray-400 mx-auto"
                      />
                      <div>
                        <p className="text-lg font-medium text-gray-700">
                          Drop your logo here or click to browse
                        </p>
                        <p className="text-sm text-gray-500">
                          Supports JPG, PNG up to 5MB
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                <input
                  type="file"
                  ref={logoFileInputRef}
                  onChange={handleLogoFileChange}
                  accept="image/*"
                  className="hidden"
                />
              </div>
            )}

            {/* Error Message */}
            {logoError && (
              <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-xl">
                <p className="text-red-600 text-sm flex items-center">
                  <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                  {logoError}
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-5">
              <ButtonSecondary
                variant="cancel"
                onClick={onClose}
                disabled={isLogoAdding}
              >
                Cancel
              </ButtonSecondary>
              <ButtonPrimary
                onClick={handleSaveLogo}
                loading={isLogoAdding}
                disabled={isLogoAdding || !logoFile || !logoName}
              >
                {!isLogoAdding && "Add Logo"}
              </ButtonPrimary>
            </div>
          </motion.div>
        </div>
      </AnimatePresence>
    </>
  );
};

export default LogoUploadeModel;
